import styled from '@emotion/styled'
import React, { useCallback, useEffect, useState } from 'react'
import Head from 'next/head'
import { useForm } from 'react-hook-form'
import { colors } from '../../../../styles/theme'
import { PlatformSelector } from '@/components/atoms/PlatformSelector'
import { VoiceChatSelector } from '@/components/atoms/VoiceChatSelector'
import {
  InputText,
  InputSelector,
  OptionsPlaytime,
} from '@/components/atoms/Inputs'
import { Platform } from '@/firebase/firestore/Platform'
import { VoiceChat } from '@/firebase/firestore/VoiceChats'
import { Sex } from '@/firebase/firestore/Sex'
import { DBDKillerSelector, DBDSurvivorSelector } from './DBDSelector'
import { ColorSelector } from '@/components/atoms/ColorSelector'
import { useLocale } from '@/hooks/useLocale'
import { ImageSelector } from '@/components/atoms/ImageSelector'
import { SexSelector } from '@/components/atoms/SexSelector'
import { DBD1 } from './DBD1'
import { DBD2 } from './DBD2'
import { DBD3 } from './DBD3'
import { ProfileCardEditorDescription } from '@/components/atoms/ProfileCardEditorDescription'
import { CREATOR, CREATORS, mapDBDCreatorInfo } from './config'
import { TemplateSelector } from '@/components/atoms/TemplateSelector'
/**
 * プラットフォーム事にフォームが違う
 *
 */
type FormField = {
  id: string
  name: string
  playStyle: string
  playTimeWeekDayBegin: string
  playTimeWeekDayEnd: string
  playTimeWeekEndBegin: string
  playTimeWeekEndEnd: string
  hitokoto: string
}

type Props = {
  onCreate: (
    data: FormField & { dbdKiller: number[]; dbdSurvivor: number[] },
    targetId: string,
    selectedSex: Sex,
    selectedPlatform: Array<Platform>,
    selectedVoiceChat: Array<VoiceChat>
  ) => void
  submitButtonDisable: boolean
  creatorParam: string | null
}

const FONT_FAMILIES = [
  {
    imagePath: '/static/images/fonts/ap.png',
    value: 'ap',
  },
  {
    imagePath: '/static/images/fonts/anko.png',
    value: 'anko',
  },
  {
    imagePath: '/static/images/fonts/smart_ui.png',
    value: 'smartfont',
  },
  {
    imagePath: '/static/images/fonts/noto_sans.png',
    value: 'notosans',
  },
] as const

const FONT_COLORS = [
  '#FD8080',
  '#FD7C24',
  '#29DDF2',
  '#C05AE0',
  '#7AA852',
  '#4C4C4C',
]

const creatorMapTargetId: Record<CREATOR, string> = {
  aisan000531: 'dbd1',
  watashiwatotem: 'dbd2',
  paultask_game: 'dbd3',
}

const creatorMapColors: Record<CREATOR, string[]> = {
  aisan000531: [
    '#000000',
    '#7F170E',
    '#173F70',
    '#B85282',
    '#AF3A1A',
    '#45516D',
    '#50AE6E',
    '#E99D21',
  ],
  watashiwatotem: ['#FFDB23', '#270809'],
  paultask_game: ['#080A2D'],
}

const creatorMapSurvivors: Record<CREATOR, string[]> = {
  aisan000531: Array(39)
    .fill(0)
    .map((_, index) => {
      return `/static/images/characters/dbd1/s_${index}.png`
    }),
  watashiwatotem: Array(39)
    .fill(0)
    .map((_, index) => {
      return `/static/images/characters/dbd2/s_${index}.png`
    }),
  paultask_game: Array(33)
    .fill(0)
    .map((_, index) => {
      return `/static/images/characters/dbd3/s_${index}.png`
    }),
}

const creatorMapKillers: Record<CREATOR, string[]> = {
  aisan000531: Array(34)
    .fill(0)
    .map((_, index) => {
      return `/static/images/characters/dbd1/k_${index}.png`
    }),
  watashiwatotem: Array(34)
    .fill(0)
    .map((_, index) => {
      return `/static/images/characters/dbd2/k_${index}.png`
    }),
  paultask_game: Array(29)
    .fill(0)
    .map((_, index) => {
      return `/static/images/characters/dbd3/k_${index}.png`
    }),
}

type FONT_FAMILY = (typeof FONT_FAMILIES)[number]['value']

const templates = Object.keys(mapDBDCreatorInfo).map((creatorName) => {
  return {
    imagePath: mapDBDCreatorInfo[creatorName]['profileCardImageURL'],
    value: creatorName,
  }
})

export const EditorDBD: React.FC<Props> = React.memo(
  ({ onCreate, submitButtonDisable, creatorParam }) => {
    const { t } = useLocale()
    const { handleSubmit, register, watch } = useForm<FormField>({
      defaultValues: {
        playTimeWeekDayBegin: '18',
        playTimeWeekDayEnd: '21',
        playTimeWeekEndBegin: '18',
        playTimeWeekEndEnd: '21',
      },
    })
    const fieldVal = watch()

    const [selectedCreator, setSelectedCreator] = useState<CREATOR>(
      CREATORS.includes(creatorParam as any)
        ? (creatorParam as CREATOR)
        : CREATORS[0]
    )
    useEffect(() => {
      if (!selectedCreator) return
      const url = new URL(window.location.href)
      url.searchParams.set('creator', selectedCreator)
      window.history.pushState(null, '', url.toString())
    }, [selectedCreator])

    const [selectedFontFamily, setSelectedFontFamily] =
      useState<FONT_FAMILY>('ap')
    const [selectedSex, setSex] = useState<Sex>(0)
    const onChangeSex = useCallback((selectedIndex: Sex) => {
      setSex(selectedIndex)
    }, [])

    const [selectedColor, setColor] = useState(0)
    const onChangeColor = useCallback((selectedIndex: number) => {
      setColor(selectedIndex)
    }, [])

    const [selectedFontColor, setFontColor] = useState(0)
    const onChangeFontColor = useCallback((selectedIndex: number) => {
      setFontColor(selectedIndex)
    }, [])

    const [selectedPlatform, setPlatform] = useState<Array<Platform>>([0])
    const onChangePlatform = useCallback(
      (selectedIndex: Array<0 | 1 | 2 | 3>) => {
        setPlatform(selectedIndex)
      },
      []
    )

    const [selectedVoiceChat, setVoiceChat] = useState<
      Array<0 | 1 | 2 | 3 | 4 | 5>
    >([])
    const onChangeVoiceChat = useCallback(
      (selectedIndex: Array<0 | 1 | 2 | 3 | 4 | 5>) => {
        setVoiceChat(selectedIndex)
      },
      []
    )

    const [selectedKiller, setKiller] = useState<Array<number>>([])
    const onChangeKiller = useCallback((selectedIndex: number[]) => {
      setKiller(selectedIndex)
    }, [])
    const [selectedSurvivor, setSurvivor] = useState<Array<number>>([])
    const onChangeSurvivor = useCallback((selectedIndex: number[]) => {
      setSurvivor(selectedIndex)
    }, [])

    const handleCreate = (data: FormField) => {
      onCreate(
        {
          ...data,
          dbdKiller: selectedKiller,
          dbdSurvivor: selectedSurvivor,
        },
        creatorMapTargetId[selectedCreator],
        2,
        selectedPlatform,
        selectedVoiceChat
      )
    }

    const jsonLdConfigs = [
      {
        '@context': 'http://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'gamee',
            item: 'https://gamee.games/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'DbD自己紹介カード作成ツール',
            item: 'https://gamee.games/me/profiles/create/dbd',
          },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': 'https://gamee.games/me/profiles/create/apex',
        },
        headline: '【DbD】かわいい自己紹介カード作成ツール',
        description:
          'DbD(デットバイデイライト)のかわいい自己紹介カードが簡単に作成・編集できるツールです！🥳🎉 プレビューを見ながら文字入れをしてあなただけの自己紹介カードをつくろう！',
        articleBody:
          'DbD(デットバイデイライト)のかわいい自己紹介カードが簡単に作成・編集できるツールです！🥳🎉 テンプレートを選ぶ カードの背景色を選ぶ 文字色を選ぶ なまえ ID 性別 プラットフォーム ボイスチャット プレイ時間帯(平日) プレイ時間帯(土日) 好きな武器 よく使うレジェンド K/D 最高ダメージ ランク ひとこと これでつくる！',
        image: ['https://gamee.games/static/images/promotes/dbd.jpg'],
        datePublished: '2022-11-20T20:34:21+09:00',
        dateModified: `${new Date().getFullYear()}-${(
          '0' +
          (new Date().getMonth() + 1)
        ).slice(-2)}-${('0' + new Date().getDate()).slice(-2)}T20:34:21+09:00`,
        author: {
          '@type': 'Person',
          name: 'あやの',
        },
        publisher: {
          '@type': 'Organization',
          name: 'gamee',
          logo: {
            '@type': 'ImageObject',
            url: 'https://gamee.games/static/images/icn_ayano.jpeg',
          },
        },
      },
    ]

    return (
      <>
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdConfigs) }}
          />
        </Head>
        <PreviewContainer>
          <PreviewCardContainer>
            {selectedCreator === 'aisan000531' && (
              <DBD1
                templateColor={selectedColor as 0 | 1 | 2 | 3}
                targetId={creatorMapTargetId[selectedCreator]}
                fontFamily={selectedFontFamily}
                uid={fieldVal.id}
                name={fieldVal.name}
                fontColor={FONT_COLORS[selectedFontColor]}
                playStyle={fieldVal.playStyle}
                platform={selectedPlatform}
                voiceChat={selectedVoiceChat}
                playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
                playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
                playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
                playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
                hitokoto={fieldVal.hitokoto}
                dbdKiller={selectedKiller}
                dbdSurvivor={selectedSurvivor}
              />
            )}
            {selectedCreator === 'watashiwatotem' && (
              <DBD2
                templateColor={selectedColor as 0 | 1 | 2 | 3}
                targetId={creatorMapTargetId[selectedCreator]}
                fontFamily={selectedFontFamily}
                uid={fieldVal.id}
                name={fieldVal.name}
                fontColor={FONT_COLORS[selectedFontColor]}
                playStyle={fieldVal.playStyle}
                platform={selectedPlatform}
                voiceChat={selectedVoiceChat}
                hitokoto={fieldVal.hitokoto}
                dbdKiller={selectedKiller}
                dbdSurvivor={selectedSurvivor}
              />
            )}
            {selectedCreator === 'paultask_game' && (
              <DBD3
                templateColor={selectedColor as 0 | 1 | 2 | 3}
                targetId={creatorMapTargetId[selectedCreator]}
                fontFamily={selectedFontFamily}
                uid={fieldVal.id}
                name={fieldVal.name}
                fontColor={FONT_COLORS[selectedFontColor]}
                platform={selectedPlatform}
                playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
                playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
                playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
                playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
                voiceChat={selectedVoiceChat}
                hitokoto={fieldVal.hitokoto}
                dbdKiller={selectedKiller}
                dbdSurvivor={selectedSurvivor}
              />
            )}
          </PreviewCardContainer>
        </PreviewContainer>
        <DescriptionSection>
          <Time>
            <time
              dateTime={'2022-11-20T20:34:21+09:00'}
              itemProp="datepublished"
            />
            <time dateTime={'2022-11-20T20:34:21+09:00'} itemProp="modified">
              {t.最終更新} : {new Date().getFullYear()}
              {t.年}
              {new Date().getMonth() + 1}
              {t.月}
              {new Date().getDate()}
              {t.日}
            </time>
          </Time>
          <H1Text>{t.DbDかわいい自己紹介カード作成ツール}</H1Text>
          <H2Text>{t.DbDかわいい自己紹介カード作成ツール説明文}</H2Text>
          <ProfileCardEditorDescription />
        </DescriptionSection>
        <FormContainer>
          <form
            onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
            onSubmit={handleSubmit(handleCreate)}
          >
            <FormItemContainer>
              <VerticalContainer>
                <SectionText>{t.テンプレートを選ぶ}</SectionText>
                <TemplateSelector
                  values={templates}
                  onChange={setSelectedCreator as any}
                  value={selectedCreator}
                />
              </VerticalContainer>
            </FormItemContainer>
            <FormItemContainer>
              <Label>{t.カードの背景色を選ぶ}</Label>
              <ColorSelector
                colors={creatorMapColors[selectedCreator]}
                value={selectedColor}
                onChange={onChangeColor}
              />
            </FormItemContainer>
            <FormItemContainer>
              <Label>文字色を選ぶ</Label>
              <ColorSelector
                colors={FONT_COLORS}
                value={selectedFontColor}
                onChange={onChangeFontColor}
              />
            </FormItemContainer>
            <FormItemContainer>
              <SectionText>{t.フォントを選ぶ}</SectionText>
              <ImageSelector
                values={FONT_FAMILIES}
                onChange={setSelectedFontFamily as any}
                value={selectedFontFamily}
              />
            </FormItemContainer>
            <FormItemContainer>
              <Label>{t.なまえ}</Label>
              <InputText {...register('name', { maxLength: 20 })} />
            </FormItemContainer>
            <FormItemContainer>
              <Label>{t.ID}</Label>
              <InputText {...register('id', { maxLength: 20 })} />
            </FormItemContainer>
            <FormItemContainer>
              <SectionText>{t.性別}</SectionText>
              <SexSelector val={selectedSex} onChange={onChangeSex} />
            </FormItemContainer>
            <FormItemContainer>
              <Label>{t.プラットフォーム}</Label>
              <PlatformSelector
                activeValues={
                  selectedCreator === 'paultask_game'
                    ? [0, 1, 3, 2]
                    : [0, 1, 2, 3, 4]
                }
                onChange={onChangePlatform}
                value={selectedPlatform}
              />
            </FormItemContainer>
            {selectedCreator !== 'paultask_game' && (
              <FormItemContainer>
                <Label>{t.プレイスタイル}</Label>
                <InputText {...register('playStyle', { maxLength: 20 })} />
              </FormItemContainer>
            )}
            <FormItemContainer>
              <Label>{t.ボイスチャット}</Label>
              <VoiceChatSelector
                onChange={onChangeVoiceChat}
                value={selectedVoiceChat}
                activeValue={[0, 1, 2, 3, 5, 4]}
              />
            </FormItemContainer>
            {selectedCreator === 'aisan000531' && (
              <FormItemContainer>
                <Label>{t.使用キラー}</Label>
                <DBDKillerSelector
                  values={creatorMapKillers[selectedCreator]}
                  onChange={onChangeKiller}
                />
              </FormItemContainer>
            )}
            {selectedCreator === 'aisan000531' && (
              <FormItemContainer>
                <Label>{t.使用サバイバー}</Label>
                <DBDSurvivorSelector
                  values={creatorMapSurvivors[selectedCreator]}
                  onChange={onChangeSurvivor}
                />
              </FormItemContainer>
            )}
            {selectedCreator === 'watashiwatotem' && (
              <FormItemContainer>
                <Label>{t.使用サバイバー}</Label>
                <DBDSurvivorSelector
                  values={creatorMapSurvivors[selectedCreator]}
                  onChange={onChangeSurvivor}
                />
              </FormItemContainer>
            )}
            {selectedCreator === 'watashiwatotem' && (
              <FormItemContainer>
                <Label>{t.使用キラー}</Label>
                <DBDKillerSelector
                  values={creatorMapKillers[selectedCreator]}
                  onChange={onChangeKiller}
                />
              </FormItemContainer>
            )}
            {selectedCreator === 'paultask_game' && (
              <FormItemContainer>
                <Label>{t.使用キラー}</Label>
                <DBDKillerSelector
                  values={creatorMapKillers[selectedCreator]}
                  onChange={onChangeKiller}
                />
              </FormItemContainer>
            )}
            {selectedCreator === 'paultask_game' && (
              <FormItemContainer>
                <Label>{t.使用サバイバー}</Label>
                <DBDSurvivorSelector
                  values={creatorMapSurvivors[selectedCreator]}
                  onChange={onChangeSurvivor}
                />
              </FormItemContainer>
            )}
            <FormItemContainer>
              <Label>{t.プレイ時間帯平日}</Label>
              <InputSelector {...register('playTimeWeekDayBegin')}>
                <OptionsPlaytime />
              </InputSelector>
              <TimeSelectorText>から</TimeSelectorText>
              <InputSelector {...register('playTimeWeekDayEnd')}>
                <OptionsPlaytime />
              </InputSelector>
            </FormItemContainer>
            <FormItemContainer>
              <Label>{t.プレイ時間帯土日}</Label>
              <InputSelector {...register('playTimeWeekEndBegin')}>
                <OptionsPlaytime />
              </InputSelector>
              <TimeSelectorText>から</TimeSelectorText>
              <InputSelector {...register('playTimeWeekEndEnd')}>
                <OptionsPlaytime />
              </InputSelector>
            </FormItemContainer>
            <FormItemContainer>
              <Label>{t.ひとこと}</Label>
              <InputText {...register('hitokoto', { maxLength: 50 })} />
            </FormItemContainer>
            <Submit
              type="submit"
              value={t.これでつくる}
              disabled={submitButtonDisable}
            />
          </form>
        </FormContainer>
      </>
    )
  }
)

const PreviewContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 1000;
`

const PreviewCardContainer = styled.div`
  background-color: ${colors.bg.primary};
  box-shadow: 0 0 4px rgba(32, 32, 32, 0.1);
`

const FormContainer = styled.div`
  padding: 32px 16px;
`

const FormItemContainer = styled.div`
  margin-bottom: 32px;
`

const DescriptionSection = styled.label`
  margin-top: 16px;
  margin-left: 16px;
  margin-right: 16px;
`

const Time = styled.div`
  color: ${colors.text.secondary};
  font-size: 12px;
`

const H1Text = styled.h1`
  margin-top: 12px;
  font-weight: bold;
  font-size: 13px;
  color: ${colors.text.primary};
  text-align: center;
  margin-bottom: 12px;
  display: block;
`

const H2Text = styled.h2`
  font-size: 13px;
  color: ${colors.text.primary};
  text-align: center;
  margin-bottom: 12px;
  display: block;
  line-height: 1.4;
`

const Label = styled.label`
  font-weight: bold;
  font-size: 18px;
  color: ${colors.text.primary};
  margin-bottom: 16px;
  display: block;
`

const TimeSelectorText = styled.div`
  margin: 8px auto;
  color: ${colors.text.primary};
  font-size: 12px;
  font-weight: bold;
`

const Submit = styled.input`
  border-radius: 20px;
  border: 3px solid transparent;
  width: 100%;
  height: 40px;
  background-color: ${colors.button.primary};
  color: #fff;
  font-weight: bold;
  display: block;
`

const VerticalContainer = styled.div`
  display: flex;
  flex-flow: column;
`

const SectionText = styled.div`
  margin-top: 20px;
  margin-bottom: 16px;
  font-weight: bold;
  font-size: 18px;
  color: ${colors.text.primary};
  display: flex;
`
